import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { api, errorText } from "@/lib/api";

const OFF_ROUTE_M = 80;
const REROUTE_GAP_MS = 15000;
const ARRIVE_M = 60;

const meters = (a, b) => {
  const R = 6371000, rad = Math.PI / 180;
  const dLat = (b[0] - a[0]) * rad, dLon = (b[1] - a[1]) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * rad) * Math.cos(b[0] * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const bearing = (a, b) => {
  const rad = Math.PI / 180;
  const y = Math.sin((b[1] - a[1]) * rad) * Math.cos(b[0] * rad);
  const x = Math.cos(a[0] * rad) * Math.sin(b[0] * rad) - Math.sin(a[0] * rad) * Math.cos(b[0] * rad) * Math.cos((b[1] - a[1]) * rad);
  return (Math.atan2(y, x) / rad + 360) % 360;
};

export function aheadPoint(p, coords, want) {
  const s = snap(p, coords);
  let prev = [coords[s.i][0] + (coords[Math.min(s.i + 1, coords.length - 1)][0] - coords[s.i][0]) * (s.t || 0), coords[s.i][1] + (coords[Math.min(s.i + 1, coords.length - 1)][1] - coords[s.i][1]) * (s.t || 0)];
  let left = want;
  for (let i = s.i + 1; i < coords.length; i++) {
    const d = meters(prev, coords[i]);
    if (d >= left) { const f = left / d; return [prev[0] + (coords[i][0] - prev[0]) * f, prev[1] + (coords[i][1] - prev[1]) * f]; }
    left -= d;
    prev = coords[i];
  }
  return coords[coords.length - 1];
}

export function snap(p, coords) {
  const k = Math.cos(p[0] * Math.PI / 180) * 111320, kl = 110540;
  let best = { d: Infinity, i: 0 };
  for (let i = 0; i < coords.length - 1; i++) {
    const ax = (coords[i][1] - p[1]) * k, ay = (coords[i][0] - p[0]) * kl;
    const bx = (coords[i + 1][1] - p[1]) * k, by = (coords[i + 1][0] - p[0]) * kl;
    const dx = bx - ax, dy = by - ay, L = dx * dx + dy * dy;
    const t = L ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / L)) : 0;
    const d = Math.hypot(ax + t * dx, ay + t * dy);
    if (d < best.d) best = { d, i, t };
  }
  return best;
}

export function useNavigation(origin) {
  const [dest, setDest] = useState(null);
  const [route, setRoute] = useState(null);
  const [pos, setPos] = useState(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [via, setVia] = useState([]);
  const [preview, setPreview] = useState(false);
  const routeRef = useRef(null), destRef = useRef(null), lastRef = useRef(0), watchRef = useRef(null), seq = useRef(0), prevRef = useRef(null), viaRef = useRef([]), previewRef = useRef(false), fromRef = useRef(null);

  const fetchRoute = useCallback(async (from, to, reroute) => {
    const id = ++seq.current;
    lastRef.current = Date.now();
    setBusy(true);
    try {
      const stops = viaRef.current.map((v) => `${v.lat},${v.lon}`).join("|");
      const { data } = await api.get("/driver/route", { params: { from_lat: from.lat, from_lon: from.lon, to_lat: to.lat, to_lon: to.lon, reroute: reroute ? 1 : 0, ...(stops ? { via: stops } : {}) } });
      if (id !== seq.current || !destRef.current) return;
      routeRef.current = data;
      setRoute(data);
      setStatus(reroute ? "Rerouted from your current position." : "");
    } catch (e) { if (id === seq.current) setStatus(errorText(e, "Could not get directions.")); }
    if (id === seq.current) setBusy(false);
  }, []);

  const onFix = useCallback((p) => {
    const here = { lat: p.coords.latitude, lon: p.coords.longitude, acc: p.coords.accuracy, heading: p.coords.heading, speed: p.coords.speed, at: p.timestamp || Date.now() };
    const prev = prevRef.current;
    if (prev) {
      const moved = meters([prev.lat, prev.lon], [here.lat, here.lon]), dt = (here.at - prev.at) / 1000;
      if (here.speed == null && dt > 0.5) here.speed = moved / dt;
      if ((here.heading == null || Number.isNaN(here.heading)) && moved > 8) here.heading = bearing([prev.lat, prev.lon], [here.lat, here.lon]);
      if ((here.heading == null || Number.isNaN(here.heading)) && prev.heading != null) here.heading = prev.heading;
    }
    if (here.heading != null && Number.isNaN(here.heading)) here.heading = null;
    if (!prev || meters([prev.lat, prev.lon], [here.lat, here.lon]) > 8 || here.at - prev.at > 10000) prevRef.current = here;
    setPos(here);
    setStatus((s) => (s.startsWith("Waiting") ? "" : s));
    const rt = routeRef.current, to = destRef.current;
    if (!rt || !to || previewRef.current) return;
    const reached = viaRef.current.filter((v) => meters([here.lat, here.lon], [v.lat, v.lon]) < ARRIVE_M * 2);
    if (reached.length) { viaRef.current = viaRef.current.filter((v) => !reached.includes(v)); setVia(viaRef.current); setStatus(`Reached ${reached[0].name || "your stop"}.`); }
    if (meters([here.lat, here.lon], [to.lat, to.lon]) < ARRIVE_M) { setStatus("You have arrived."); return; }
    const off = snap([here.lat, here.lon], rt.coords).d;
    if (off > Math.max(OFF_ROUTE_M, here.acc || 0) && Date.now() - lastRef.current > REROUTE_GAP_MS) fetchRoute(here, to, true);
  }, [fetchRoute]);

  const stopWatch = () => { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; };
  const watch = useCallback(() => {
    if (!navigator.geolocation || watchRef.current != null) return;
    watchRef.current = navigator.geolocation.watchPosition(onFix,
      (e) => setStatus(e.code === 1 ? "Location permission denied. Route will not follow you." : "Waiting for GPS signal…"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 });
  }, [onFix]);

  const start = useCallback((spot, opts = {}) => {
    destRef.current = spot;
    routeRef.current = null;
    viaRef.current = opts.via || [];
    previewRef.current = !!opts.from;
    fromRef.current = opts.from || null;
    setVia(viaRef.current);
    setPreview(!!opts.from);
    setDest(spot);
    setRoute(null);
    setStatus("");
    fetchRoute(opts.from || pos || origin, spot, false);
    if (opts.from) return;
    if (!navigator.geolocation) { setStatus("Live GPS is not available in this browser. Route drawn from your IP location."); return; }
    watch();
  }, [fetchRoute, watch, origin, pos]);

  const addStop = useCallback((spot, along) => {
    if (!destRef.current || viaRef.current.length >= 5 || viaRef.current.some((v) => v.id === spot.id)) return false;
    const c = routeRef.current?.coords;
    const cum = [0];
    if (c) for (let i = 1; i < c.length; i++) cum.push(cum[i - 1] + meters(c[i - 1], c[i]));
    const at = (v) => { if (!c) return v.along_km ?? along ?? 0; const s = snap([v.lat, v.lon], c); return cum[s.i] + (cum[Math.min(s.i + 1, c.length - 1)] - cum[s.i]) * (s.t || 0); };
    const list = [...viaRef.current, spot].map((v) => ({ ...v, _at: at(v) })).sort((a, b) => a._at - b._at);
    viaRef.current = list;
    setVia(list);
    fetchRoute(fromRef.current || pos || origin, destRef.current, false);
    return true;
  }, [fetchRoute, pos, origin]);

  const removeStop = useCallback((id) => {
    viaRef.current = viaRef.current.filter((v) => v.id !== id);
    setVia(viaRef.current);
    if (destRef.current) fetchRoute(fromRef.current || pos || origin, destRef.current, false);
  }, [fetchRoute, pos, origin]);

  const stop = useCallback(() => { seq.current++; destRef.current = null; routeRef.current = null; viaRef.current = []; previewRef.current = false; fromRef.current = null; setVia([]); setPreview(false); setDest(null); setRoute(null); setStatus(""); setBusy(false); }, []);
  const recalc = useCallback(() => { if (destRef.current) fetchRoute(fromRef.current || pos || origin, destRef.current, true); }, [fetchRoute, pos, origin]);
  useEffect(() => stopWatch, []);

  const progress = useMemo(() => {
    if (!route) return null;
    const c = route.coords;
    const cum = [0];
    for (let i = 1; i < c.length; i++) cum.push(cum[i - 1] + meters(c[i - 1], c[i]));
    const total = cum[cum.length - 1] || 1;
    const s = pos && !preview ? snap([pos.lat, pos.lon], c) : { i: 0, t: 0 };
    const done = cum[s.i] + (cum[Math.min(s.i + 1, c.length - 1)] - cum[s.i]) * (s.t || 0);
    const next = route.steps.findIndex((st) => st.idx > s.i) ;
    const left = Math.max(0, total - done);
    return { next: next < 0 ? route.steps.length - 1 : next, left_m: left, left_s: route.duration_s * (left / total), to_next_m: next < 0 ? left : Math.max(0, cum[route.steps[next].idx] - done) };
  }, [route, pos, preview]);

  return { dest, route, pos, status, busy, progress, start, stop, recalc, track: watch, via, preview, addStop, removeStop };
}
